import { Flex, Image, ImageProps, toaster } from 'components'
import { isEqual } from 'lodash-es'
import { motion } from 'motion/react'
import { useContext, useEffect, useState } from 'react'
import { HelperLeadProperty, TemplateRoute } from 'types'
import { sleep } from 'utils'
import ActionButtons, { ActionButtonContainer } from '../components/ActionButtons'
import { SessionContext, useExplorerConfig } from '../components/providers'
import { SView } from '../components/scaled'
import { TraitProgressPanel } from '../components/trait-selection'
import TraitStep from '../components/trait-selection/TraitStep'
import { AnimationDuration, SCREEN_TRANSITION } from '../utils/globals'
import { EXPLORER_DIMENSIONS } from '../utils/helpers'
import { useLayoutMap } from '../utils/layoutUtils'
import {
  getTrait,
  useAllBackgrounds,
  useCurrentBackgrounds,
  useDefaultBackground,
  useRemainingRouting,
} from '../utils/traitUtils'

const { height, width } = EXPLORER_DIMENSIONS.traits

export default function TraitSelectionView() {
  const { templateDocs } = useExplorerConfig()
  const { routeSteps, getLanguageValue, session, updateRouteSteps, mergeSession, status } =
    useContext(SessionContext)
  const defaultBackground = useDefaultBackground()
  const allBackgrounds = useAllBackgrounds()
  const layoutMap = useLayoutMap()
  const remainingRouting = useRemainingRouting()

  const [traits, setTraitSelections] = useState(session?.traits ?? [])
  const currentBackgrounds = useCurrentBackgrounds(traits)
  const currentBackground = currentBackgrounds[currentBackgrounds.length - 1]
  const [lastActiveBackground, setLastActiveBackground] = useState(currentBackground?.url)

  const currentStep = routeSteps[routeSteps.length - 1]
  const currentStepType = currentStep?.type
  const currentTrait =
    currentStepType === 'trait' ? getTrait(templateDocs!, currentStep.key) : undefined
  const currentSelectedValues = (() => {
    const selections = traits
    if (!selections || !currentTrait?.values) return []

    return currentTrait.values.filter(p =>
      selections?.some(s =>
        s.traitId ? s.traitId === p.id : s.title === getLanguageValue(p.title)
      )
    )
  })()

  const disableNext = (() => {
    if (!currentTrait) return true

    const { lead } = session || {}
    const { leadField } = currentTrait
    if (leadField && lead) {
      const value = lead[leadField as HelperLeadProperty] ?? lead.custom?.[leadField]
      if (value != null) return false
    }

    if (currentSelectedValues.length) return false

    return true
  })()

  useEffect(() => {
    if (currentStepType !== 'layout') return

    const layout = layoutMap[currentStep.key]
    if (!layout) {
      toaster.create({ title: 'Layout not found', type: 'error' })
      updateRouteSteps(routeSteps.slice(0, -1))
      return
    }
  }, [currentStepType])

  useEffect(() => {
    if (currentBackground?.url === lastActiveBackground) return

    sleep(AnimationDuration[fadeDuration]).then(() =>
      setLastActiveBackground(currentBackground?.url)
    )
  }, [currentBackground])

  useEffect(() => {
    ;(async () => {
      if (!isEqual(session?.traits, traits)) {
        if (status !== 'active') await sleep(SCREEN_TRANSITION)
        setTraitSelections(session?.traits ?? [])
      }
    })()
  }, [session, status])

  function handleNext() {
    if (!remainingRouting) return
    if (!isEqual(traits, session?.traits)) mergeSession({ traits })

    const firstValue = currentSelectedValues[0]
    const valueRoute = remainingRouting.valueRoutes?.find(r => r.fromIds.includes(firstValue.id))
    const route = valueRoute?.to || remainingRouting.default
    if (route == null) return toaster.create({ title: 'No route found', type: 'error' })

    const { key, type } = route
    return updateRouteSteps([...routeSteps, { key, type }])
  }

  function handleGoBackTo({ key }: TemplateRoute) {
    let updatedSteps: TemplateRoute[] = []
    for (let step of routeSteps) {
      updatedSteps.push(step)
      if (step.key === key) break
    }
    updateRouteSteps(updatedSteps)
  }

  function handleBack() {
    updateRouteSteps(routeSteps.slice(0, -1))
  }

  return (
    <Flex alignItems="center" flex={1} justifyContent="center" overflow="hidden">
      <BackgroundImageDisplay
        active={!currentBackground}
        lastActive={!lastActiveBackground}
        src={defaultBackground}
      />

      {allBackgrounds.map(({ id, url }, i) => (
        <BackgroundImageDisplay
          key={i}
          active={id === currentBackground?.id}
          lastActive={url === lastActiveBackground}
          src={url}
        />
      ))}

      <SView zIndex={3} gap={15}>
        <ActionButtonContainer />
        <SView h={height} w={width} direction="row" gap={15}>
          <TraitProgressPanel goBackTo={handleGoBackTo} w={350} />
          <TraitStep selections={traits} setSelections={setTraitSelections} />
        </SView>
        <ActionButtons
          nextProps={{ disabled: disableNext }}
          onBack={routeSteps.length > 1 ? handleBack : undefined}
          onNext={handleNext}
        />
      </SView>
    </Flex>
  )
}

interface BackgroundImageProps extends ImageProps {
  active: boolean
  lastActive: boolean
}

const fadeDuration = 'med'

function BackgroundImageDisplay({ active, lastActive, ...rest }: BackgroundImageProps) {
  if (!rest.src) return null

  return (
    <motion.div
      transition={{ duration: AnimationDuration[fadeDuration] / 1000 }}
      style={{ position: 'absolute', inset: 0, zIndex: active ? 0 : lastActive ? -1 : -2 }}
      animate={active || lastActive ? 'visible' : 'hidden'}
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
    >
      <Image height="100%" width="100%" objectFit="cover" {...rest} />
    </motion.div>
  )
}
